import React, { useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { AuthContext } from "@/App";
import { rupiah, num, formatDate, packDisplay } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowUpFromLine, Receipt, Truck, Undo2, CheckCircle2, AlertTriangle, Package, Users, TrendingUp, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const [period, setPeriod] = useState("today");
  const [summary, setSummary] = useState(null);
  const [salesRows, setSalesRows] = useState([]);
  const [stock, setStock] = useState(null);
  const [opnameStats, setOpnameStats] = useState(null);

  const load = useCallback(async () => {
    try {
      if (user?.role === "sales") {
        const s = await api.get(`/dashboard/summary?period=${period}`);
        setSummary(s.data); setSalesRows([]); setStock(null); setOpnameStats(null);
        return;
      }
      const [s, ss, st, op] = await Promise.all([
        api.get(`/dashboard/summary?period=${period}`),
        api.get("/dashboard/sales-summary"),
        api.get("/dashboard/stock-summary"),
        api.get("/opnames/stats"),
      ]);
      setSummary(s.data); setSalesRows(ss.data); setStock(st.data); setOpnameStats(op.data);
    } catch (e) {
      console.error("Gagal memuat dashboard:", e);
    }
  }, [period, user?.role]);

  useEffect(() => { load(); }, [load]);

  if (summary?.scoped) {
    return (
      <div className="space-y-8" data-testid="page-dashboard">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Dashboard Sales</div>
            <h1 className="font-display text-3xl font-bold text-slate-900">Armada Saya — {summary.sales_name}</h1>
            <p className="text-slate-500 text-sm mt-1">Ringkasan penjualan, retur & stok armada Anda sendiri.</p>
          </div>
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList data-testid="period-tabs">
              <TabsTrigger value="today" data-testid="period-today">Hari Ini</TabsTrigger>
              <TabsTrigger value="week" data-testid="period-week">7 Hari</TabsTrigger>
              <TabsTrigger value="month" data-testid="period-month">30 Hari</TabsTrigger>
              <TabsTrigger value="all" data-testid="period-all">Semua</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard testid="metric-my-sales" icon={<Receipt />} label="Penjualan Saya (Rp)" value={rupiah(summary.invoice_total)} accent="bg-blue-50 text-blue-900" />
          <MetricCard testid="metric-my-stock" icon={<Truck />} label="Stok Armada Saya" value={rupiah(summary.my_stock_value)} accent="bg-indigo-50 text-indigo-900" />
          <MetricCard testid="metric-my-returns" icon={<Undo2 />} label="Retur Saya" value={rupiah(summary.returns_total)} accent="bg-amber-50 text-amber-900" />
          <MetricCard testid="metric-my-receivable" icon={<AlertTriangle />} label="Piutang Saya" value={rupiah(summary.receivable_total)} accent="bg-rose-50 text-rose-900" />
        </div>
        {summary.tempo_reminders?.length > 0 && (
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-5" data-testid="tempo-reminders">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div className="font-display font-bold text-amber-900">Pengingat Jatuh Tempo Nota ({summary.tempo_reminders.length})</div>
            </div>
            <div className="grid gap-2">
              {summary.tempo_reminders.map((r) => (
                <div key={r.invoice_id} className={`flex items-center gap-3 rounded border p-3 text-sm ${r.overdue ? "bg-rose-50 border-rose-300" : "bg-white border-amber-200"}`}>
                  <span className="font-mono text-xs">{r.trx_no}</span>
                  <span className="font-medium">{r.customer}</span>
                  <span className={`ml-auto font-mono text-xs font-bold ${r.overdue ? "text-rose-700" : "text-amber-700"}`}>
                    {r.overdue ? `LEWAT ${-r.days} HARI` : r.days === 0 ? "JATUH TEMPO HARI INI" : `${r.days} hari lagi`}
                  </span>
                  <span className="text-xs text-slate-500 font-mono">{formatDate(r.due_date)}</span>
                  <span className="font-mono font-bold">{rupiah(r.total)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Stok Armada Saya</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm" data-testid="table-my-stock">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-4">SKU</th><th className="py-2 pr-4">Barang</th><th className="py-2 pr-4 text-right">Qty</th><th className="py-2 text-right">Nilai</th>
                </tr>
              </thead>
              <tbody>
                {summary.my_stock.map((s) => (
                  <tr key={s.sku} className="border-b border-slate-100">
                    <td className="py-2 pr-4 font-mono text-xs">{s.sku}</td>
                    <td className="py-2 pr-4">{s.name}</td>
                    <td className="py-2 pr-4 text-right font-mono">{num(s.qty)} {s.unit}{packDisplay(s.qty, s.unit, s.packs) && <div className="text-[10px] text-slate-400">{packDisplay(s.qty, s.unit, s.packs)}</div>}</td>
                    <td className="py-2 text-right font-mono">{rupiah(s.value)}</td>
                  </tr>
                ))}
                {summary.my_stock.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-slate-400 text-sm">Tidak ada stok di armada Anda</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    );
  }

  const bal = summary?.is_balanced;

  return (
    <div className="space-y-8" data-testid="page-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Neraca Barang</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Dashboard Utama</h1>
          <p className="text-slate-500 text-sm mt-1">Ringkasan pergerakan barang & armada secara real time.</p>
        </div>
        <div className="flex items-center gap-3">
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList data-testid="period-tabs">
              <TabsTrigger value="today" data-testid="period-today">Hari Ini</TabsTrigger>
              <TabsTrigger value="week" data-testid="period-week">7 Hari</TabsTrigger>
              <TabsTrigger value="month" data-testid="period-month">30 Hari</TabsTrigger>
              <TabsTrigger value="all" data-testid="period-all">Semua</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Status Bar */}
      {summary && (
        <div
          data-testid="neraca-status"
          className={`rounded-lg border-2 p-6 flex items-center gap-5 ${
            bal ? "bg-emerald-50 border-emerald-300" : "bg-rose-50 border-rose-300"
          }`}
        >
          <div className={`h-14 w-14 rounded-full grid place-items-center ${bal ? "bg-emerald-500" : "bg-rose-500"}`}>
            {bal ? <CheckCircle2 className="h-7 w-7 text-white" /> : <AlertTriangle className="h-7 w-7 text-white" />}
          </div>
          <div className="flex-1">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Status Neraca</div>
            <div className={`font-display text-2xl font-bold ${bal ? "text-emerald-800" : "text-rose-800"}`}>
              {bal ? "STOK TERKONTROL — SEIMBANG" : "PERLU PEMERIKSAAN — ADA SELISIH"}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Selisih Nilai</div>
            <div className={`font-mono text-xl font-bold ${bal ? "text-emerald-800" : "text-rose-800"}`}>
              {rupiah(Math.abs(summary.neraca_variance))}
            </div>
          </div>
        </div>
      )}

      {/* Metric Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard testid="metric-outbound" icon={<ArrowUpFromLine />} label="Barang Keluar" value={rupiah(summary.outbound_total)} accent="bg-slate-900 text-white" />
          <MetricCard testid="metric-invoice" icon={<Receipt />} label="Penjualan (Rp)" value={rupiah(summary.invoice_total)} accent="bg-blue-50 text-blue-900" />
          <MetricCard testid="metric-armada" icon={<Truck />} label="Stok Armada" value={rupiah(summary.armada_stock_value)} accent="bg-indigo-50 text-indigo-900" />
          <MetricCard testid="metric-returns" icon={<Undo2 />} label="Barang Kembali" value={rupiah(summary.returns_to_warehouse + summary.armada_returns_total)} accent="bg-amber-50 text-amber-900" />
        </div>
      )}

      {/* Saldo Awal */}
      {summary && (
        <Card data-testid="opening-summary">
          <CardContent className="py-4 flex items-center gap-6 flex-wrap">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Saldo Awal</div>
              <div className="text-sm text-slate-600">Posisi stok sebelum aplikasi dipakai (movement OPENING_BALANCE)</div>
            </div>
            <div className="flex gap-8 ml-auto">
              <div><div className="text-xs font-mono uppercase text-slate-500">Gudang</div><div className="font-mono font-bold" data-testid="opening-warehouse">{rupiah(summary.opening_warehouse_value)}</div></div>
              <div><div className="text-xs font-mono uppercase text-slate-500">Armada</div><div className="font-mono font-bold" data-testid="opening-armada">{rupiah(summary.opening_armada_value)}</div></div>
              <div><div className="text-xs font-mono uppercase text-slate-500">Total Persediaan Awal</div><div className="font-mono font-bold text-emerald-700" data-testid="opening-total">{rupiah(summary.opening_total_value)}</div></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ringkasan Opname Armada */}
      {opnameStats && (
        <Card data-testid="opname-summary">
          <CardContent className="py-4 flex items-center gap-6 flex-wrap">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1"><ClipboardCheck className="h-3.5 w-3.5" /> Stock Opname Armada</div>
              <div className="text-sm text-slate-600">Rekap selisih stok fisik vs sistem di armada</div>
            </div>
            <div className="flex gap-8 ml-auto items-center flex-wrap">
              <div><div className="text-xs font-mono uppercase text-slate-500">Pending Approval</div><div className={`font-mono font-bold ${opnameStats.pending_count > 0 ? "text-amber-700" : ""}`} data-testid="opname-pending-count">{num(opnameStats.pending_count)}</div></div>
              <div><div className="text-xs font-mono uppercase text-slate-500">Selisih Hari Ini</div><div className="font-mono font-bold" data-testid="opname-today-count">{num(opnameStats.today_count)} item</div></div>
              <div><div className="text-xs font-mono uppercase text-slate-500">Nilai Kerugian</div><div className="font-mono font-bold text-rose-700" data-testid="opname-loss-value">{rupiah(opnameStats.loss_value)}</div></div>
              {opnameStats.top_sales && <div><div className="text-xs font-mono uppercase text-slate-500">Tersering Selisih</div><div className="font-mono font-bold" data-testid="opname-top-sales">{opnameStats.top_sales.name} ({num(opnameStats.top_sales.count)}×)</div></div>}
              <Link to="/opname" className="text-sm font-medium text-slate-900 underline underline-offset-4" data-testid="link-opname">Lihat Opname →</Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reminder Jatuh Tempo */}
      {summary?.tempo_reminders?.length > 0 && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-5" data-testid="tempo-reminders">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <div className="font-display font-bold text-amber-900">Pengingat Jatuh Tempo Nota ({summary.tempo_reminders.length})</div>
          </div>
          <div className="grid gap-2">
            {summary.tempo_reminders.map((r) => (
              <div key={r.invoice_id} className={`flex items-center gap-3 rounded border p-3 text-sm ${r.overdue ? "bg-rose-50 border-rose-300" : "bg-white border-amber-200"}`}>
                <span className="font-mono text-xs">{r.trx_no}</span>
                <span className="font-medium">{r.customer}</span>
                <span className="text-slate-500 text-xs">{r.sales_name}</span>
                <span className={`ml-auto font-mono text-xs font-bold ${r.overdue ? "text-rose-700" : "text-amber-700"}`}>
                  {r.overdue ? `LEWAT ${-r.days} HARI` : r.days === 0 ? "JATUH TEMPO HARI INI" : `${r.days} hari lagi`}
                </span>
                <span className="text-xs text-slate-500 font-mono">{formatDate(r.due_date)}</span>
                <span className="font-mono font-bold">{rupiah(r.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Neraca — ringkas, detail di halaman Neraca & Rekonsiliasi */}
      {summary && (
        <Card data-testid="neraca-compact">
          <CardContent className="py-4 flex items-center gap-6 flex-wrap">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Persamaan Neraca Barang</div>
              <div className="text-sm text-slate-600">Stok armada seharusnya (transaksi) vs aktual (ledger)</div>
            </div>
            <div className="flex gap-8 ml-auto items-center flex-wrap">
              <div><div className="text-xs font-mono uppercase text-slate-500">Seharusnya</div><div className="font-mono font-bold" data-testid="neraca-expected">{rupiah(summary.armada_start_value + summary.expected_armada_delta)}</div></div>
              <div><div className="text-xs font-mono uppercase text-slate-500">Aktual (Ledger)</div><div className="font-mono font-bold" data-testid="neraca-actual">{rupiah(summary.armada_stock_value)}</div></div>
              <div><div className="text-xs font-mono uppercase text-slate-500">Selisih</div><div className={`font-mono font-bold ${bal ? "text-emerald-700" : "text-rose-700"}`} data-testid="neraca-variance">{rupiah(summary.neraca_variance)}</div></div>
              <Link to="/reconciliation" className="text-sm font-medium text-slate-900 underline underline-offset-4" data-testid="link-reconciliation">Buka Audit Neraca →</Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sales Dashboard */}
      <Card>
        <CardHeader className="flex-row items-center gap-3 space-y-0">
          <Users className="h-5 w-5 text-slate-500" />
          <CardTitle className="font-display text-lg">Kinerja Sales / Armada</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-sales-summary">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-4">Sales</th>
                  <th className="py-2 pr-4 text-right">Barang Keluar</th>
                  <th className="py-2 pr-4 text-right">Terjual (COGS)</th>
                  <th className="py-2 pr-4 text-right">Kembali Gudang</th>
                  <th className="py-2 pr-4 text-right">Sisa Sistem</th>
                  <th className="py-2 pr-4 text-right">Sisa Fisik</th>
                  <th className="py-2 pr-4 text-right">Selisih</th>
                </tr>
              </thead>
              <tbody>
                {salesRows.map((r) => (
                  <tr key={r.sales_id} className="border-b border-slate-100">
                    <td className="py-3 pr-4">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.code}</div>
                    </td>
                    <td className="py-3 pr-4 text-right font-mono">{rupiah(r.outbound_value)}</td>
                    <td className="py-3 pr-4 text-right font-mono">{rupiah(r.sold_cogs)}</td>
                    <td className="py-3 pr-4 text-right font-mono">{rupiah(r.armada_return_value)}</td>
                    <td className="py-3 pr-4 text-right font-mono">{rupiah(r.expected_stock_value)}</td>
                    <td className="py-3 pr-4 text-right font-mono">{rupiah(r.actual_stock_value)}</td>
                    <td className={`py-3 pr-4 text-right font-mono font-bold ${Math.abs(r.variance) < 1 ? "text-emerald-700" : "text-rose-700"}`}>
                      {rupiah(r.variance)}
                    </td>
                  </tr>
                ))}
                {salesRows.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-slate-400 text-sm">Belum ada data sales</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Stock Overview */}
      {stock && (
        <div className="grid md:grid-cols-3 gap-4">
          <StockCard icon={<Package />} label="Total SKU Aktif" value={num(stock.total_sku)} tone="slate" />
          <StockCard icon={<TrendingUp />} label="Nilai Persediaan Total" value={rupiah(stock.total_inventory_value)} tone="emerald" />
          <StockCard icon={<Truck />} label="Nilai di Armada" value={rupiah(stock.armada_stock_value)} tone="indigo" />
          <Card className="md:col-span-3">
            <CardHeader><CardTitle className="text-base">Peringatan Stok</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Stok Kosong</div>
                {stock.zero_stock.length === 0 ? <div className="text-sm text-slate-400">Tidak ada</div> :
                  stock.zero_stock.map((p) => <Badge key={p.sku} className="mr-1 mb-1 bg-rose-100 text-rose-800 border-rose-300 font-normal">{p.sku} · {p.name}</Badge>)
                }
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Stok Menipis</div>
                {stock.low_stock.length === 0 ? <div className="text-sm text-slate-400">Tidak ada</div> :
                  stock.low_stock.map((p) => <Badge key={p.sku} className="mr-1 mb-1 bg-amber-100 text-amber-800 border-amber-300 font-normal">{p.sku} · {p.stock}/{p.min_stock}</Badge>)
                }
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-lg border border-slate-200 p-5">
      <div className={`h-10 w-10 rounded grid place-items-center mb-4 ${accent}`}>{React.cloneElement(icon, { className: "h-5 w-5" })}</div>
      <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono font-bold text-xl text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function StockCard({ icon, label, value, tone }) {
  const t = { slate: "bg-slate-900 text-white", emerald: "bg-emerald-500 text-slate-900", indigo: "bg-indigo-500 text-white" }[tone];
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <div className={`h-10 w-10 rounded grid place-items-center mb-3 ${t}`}>{React.cloneElement(icon, { className: "h-5 w-5" })}</div>
      <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono font-bold text-xl mt-1">{value}</div>
    </div>
  );
}
